import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR, formatDate, statusClass, toInputDate } from '../utils/api';

const STATUSES = ['All', 'Incoming', 'Ongoing', 'Completed', 'Dropped'];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [status,   setStatus]   = useState('All');
  const [search,   setSearch]   = useState('');
  const [showAdd,  setShowAdd]  = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/projects'), api.get('/clients')])
      .then(([p,c]) => { setProjects(p); setClients(c); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = projects.filter(p => {
    const matchStatus = status === 'All' || p.status === status;
    const matchSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.client_name.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const singleProjects   = projects.filter(p => p.project_type !== 'retainer');
  const retainerProjects = projects.filter(p => p.project_type === 'retainer');
  const totalValue       = singleProjects.filter(p => ['Incoming','Ongoing'].includes(p.status)).reduce((s,p) => s+(p.total_payable||0), 0);
  const totalReceived    = singleProjects.reduce((s,p) => s+(p.net_received||0), 0);
  const totalOutstanding = singleProjects.filter(p => ['Incoming','Ongoing'].includes(p.status)).reduce((s,p) => s+(p.outstanding||0), 0);
  const retainerMRR      = retainerProjects.filter(p => p.status === 'Ongoing').reduce((s,p) => s+(p.monthly_value||0), 0);
  const retainerMRRGst   = retainerProjects.filter(p => p.status === 'Ongoing').reduce((s,p) => s+(p.monthly_value||0)+(p.monthly_gst||0), 0);

  return (
    <div className="page-content">
      <div className="topbar">
        <div className="topbar-title">Projects</div>
        <div className="topbar-right">
          <input style={{padding:'5px 10px',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',fontSize:12,width:180,fontFamily:'inherit'}}
            placeholder="Search projects…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ New Project</button>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-5">
        <div className="kpi-card"><div className="kpi-label">Total Projects</div><div className="kpi-value">{projects.length}</div><div className="kpi-sub"><strong>{projects.filter(p=>p.status==='Ongoing').length}</strong> ongoing</div></div>
        <div className="kpi-card accent"><div className="kpi-label">Active Pipeline</div><div className="kpi-value red">{formatINR(totalValue)}</div><div className="kpi-sub">single projects only</div></div>
        <div className="kpi-card"><div className="kpi-label">Total Received</div><div className="kpi-value green">{formatINR(totalReceived)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Total Outstanding</div><div className="kpi-value danger">{formatINR(totalOutstanding)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Retainer MRR</div><div className="kpi-value" style={{color:'var(--info)'}}>{formatINR(retainerMRR)}</div><div className="kpi-sub">{formatINR(retainerMRRGst)} incl. GST · <strong>{retainerProjects.filter(p=>p.status==='Ongoing').length}</strong> active retainers</div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">All Projects</span>
          <div className="filter-tabs">
            {STATUSES.map(s => (
              <button key={s} className={`filter-tab ${status===s?'active':''}`} onClick={() => setStatus(s)}>{s}</button>
            ))}
          </div>
        </div>
        {loading ? <div className="loading-state">Loading…</div> : filtered.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">📁</div>{projects.length===0?'No projects yet. Create one above.':'No results.'}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Project</th><th>Type</th><th>Status</th><th>Start Date</th>
                <th className="num">Value (ex-GST)</th>
                <th className="num">+ GST</th>
                <th className="num">Received</th>
                <th className="num">Outstanding</th>
                <th>Collection</th>
              </tr></thead>
              <tbody>
                {filtered.map(p => {
                  const isRetainer = p.project_type === 'retainer';
                  const pct = p.total_payable > 0 ? Math.min(100, Math.round((p.net_received||0)/p.total_payable*100)) : 0;
                  return (
                    <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                      <td>
                        <div style={{fontWeight:600,color:'var(--text-dark)'}}>
                          {p.name}
                          {isRetainer && !!p.retainer_needs_reconciliation && (
                            <span className="badge badge-red" style={{marginLeft:6,fontSize:9}} title="A past month was billed but never marked collected">⚠ Reconcile</span>
                          )}
                        </div>
                        <div style={{fontSize:10,color:'var(--text-light)'}}>{p.client_name}</div>
                      </td>
                      <td><span className={`badge ${isRetainer ? 'badge-blue' : 'badge-gray'}`}>{isRetainer ? 'Retainer' : 'Single'}</span></td>
                      <td><span className={`badge ${statusClass(p.status)}`}>{p.status}</span></td>
                      <td style={{fontSize:11,color:'var(--text-light)'}}>{formatDate(p.start_date)}</td>
                      {isRetainer ? (
                        <>
                          <td className="num">{formatINR(p.monthly_value)}<span style={{color:'var(--text-light)',fontSize:10}}>/mo</span></td>
                          <td className="num" style={{color:'var(--text-light)'}}>{formatINR(p.monthly_gst)}</td>
                          <td className="num" colSpan={2} style={{textAlign:'left'}}>
                            {!p.current_cycle_period ? (
                              <span style={{color:'var(--text-light)'}}>Not billed yet</span>
                            ) : p.current_cycle_collected ? (
                              <span style={{color:'var(--success)',fontWeight:600}}>{p.current_cycle_period} collected</span>
                            ) : p.current_cycle_billed ? (
                              <span style={{color:'var(--warning)',fontWeight:600}}>{p.current_cycle_period} billed, pending</span>
                            ) : (
                              <span style={{color:'var(--danger)',fontWeight:600}}>{p.current_cycle_period} not billed</span>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="num">{formatINR(p.value)}</td>
                          <td className="num" style={{color:'var(--text-light)'}}>{formatINR(p.gst)}</td>
                          <td className="num" style={{color:'var(--success)',fontWeight:600}}>{formatINR(p.net_received)}</td>
                          <td className="num" style={{color:p.outstanding>0?'var(--danger)':'var(--success)',fontWeight:700}}>{formatINR(p.outstanding)}</td>
                        </>
                      )}
                      <td>
                        {!isRetainer && (
                          <div className="progress-wrap">
                            <div className={`progress-bar ${pct>=100?'full':''}`} style={{width:`${pct}%`}} />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && <AddProjectModal clients={clients} onClose={() => setShowAdd(false)} onSaved={(id) => { setShowAdd(false); navigate(`/projects/${id}`); }} />}
    </div>
  );
}

function AddProjectModal({ clients, onClose, onSaved }) {
  const [form, setForm] = useState({ name:'', project_type:'single', client_id:'', status:'Incoming', start_date:'', end_date:'', link:'', notes:'', paymentAdvance:50, paymentInterim:25, paymentFinal:25, invoicing_timing:'end' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  function setF(k,v) { setForm(f=>({...f,[k]:v})); }
  const isRetainer = form.project_type === 'retainer';

  async function handleSave() {
    if (!form.name)      { setError('Project name is required.'); return; }
    if (!form.client_id) { setError('Client is required.'); return; }
    setSaving(true); setError('');
    try {
      const result = await api.post('/projects', form);
      onSaved(result.id);
    } catch(err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-header"><div className="modal-title">New Project</div></div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group"><label className="form-label">Project Name *</label>
            <input className="form-input" value={form.name} onChange={e=>setF('name',e.target.value)} autoFocus /></div>

          <div className="form-group">
            <label className="form-label">Project Type</label>
            <div className="form-row">
              <label className={'type-radio' + (!isRetainer ? ' active' : '')}>
                <input type="radio" name="project_type" checked={!isRetainer} onChange={()=>setF('project_type','single')} />
                Single project
              </label>
              <label className={'type-radio' + (isRetainer ? ' active' : '')}>
                <input type="radio" name="project_type" checked={isRetainer} onChange={()=>setF('project_type','retainer')} />
                Retainer
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{flex:2}}><label className="form-label">Client *</label>
              <select className="form-select" value={form.client_id} onChange={e=>setF('client_id',e.target.value)}>
                <option value="">Select client…</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e=>setF('status',e.target.value)}>
                {['Incoming','Ongoing','Completed','Dropped'].map(s=><option key={s}>{s}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Start Date</label>
              <input className="form-input" type="date" value={form.start_date} onChange={e=>setF('start_date',e.target.value)} /></div>
            <div className="form-group"><label className="form-label">End Date {isRetainer && <span style={{fontWeight:400,color:'var(--text-light)'}}>(optional — leave open)</span>}</label>
              <input className="form-input" type="date" value={form.end_date} onChange={e=>setF('end_date',e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Drive Link</label>
            <input className="form-input" value={form.link} onChange={e=>setF('link',e.target.value)} placeholder="https://drive.google.com/…" /></div>

          {isRetainer ? (
            <div className="form-group">
              <label className="form-label">Invoicing Date</label>
              <select className="form-select" value={form.invoicing_timing} onChange={e=>setF('invoicing_timing',e.target.value)}>
                <option value="start">Beginning of month (billed in advance)</option>
                <option value="end">End of month (billed after the month)</option>
              </select>
              <div className="type-hint">Replaces advance / interim / final — retainers bill the same way every month.</div>
            </div>
          ) : (
            <div className="form-group"><label className="form-label">Payment Terms (Advance / Interim / Final %)</label>
              <div className="form-row">
                <input className="form-input" type="number" value={form.paymentAdvance} onChange={e=>setF('paymentAdvance',+e.target.value)} />
                <input className="form-input" type="number" value={form.paymentInterim} onChange={e=>setF('paymentInterim',+e.target.value)} />
                <input className="form-input" type="number" value={form.paymentFinal}   onChange={e=>setF('paymentFinal',  +e.target.value)} />
              </div></div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Creating…':'Create Project'}</button>
        </div>
      </div>
    </div>
  );
}
